/**
 * PilotWelcomePage — Post-Stripe redirect for manually-onboarded pilots (e.g. Recludo).
 *
 * This is a public, no-auth page shown after a one-off Stripe payment for a pilot
 * where the subscriber is NOT routed into self-service onboarding. The customer is
 * provisioned manually via the admin portal, so this page must NOT trigger any
 * account-creation flow — it exists purely to set expectations ("we'll take it from here")
 * and prevent "I paid, where do I log in?" support tickets.
 *
 * Route: /welcome/pilot (registered in AppRoutes.tsx)
 */

import React from 'react';
import { Mail, ShieldCheck } from 'lucide-react';

const PilotWelcomePage: React.FC = () => {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

        .pw-root {
          --pw-bg: #080810;
          --pw-text: #f0f0f0;
          --pw-text-2: #a0a6b2;
          --pw-text-3: #8a9199;
          --pw-accent: #7DD3FC;
          --pw-font: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
          --pw-ease: cubic-bezier(0.16, 1, 0.3, 1);

          font-family: var(--pw-font);
          background: var(--pw-bg);
          color: var(--pw-text);
          -webkit-font-smoothing: antialiased;
          min-height: 100vh;
          position: relative;
          overflow-x: hidden;
        }
        .pw-root *, .pw-root *::before, .pw-root *::after { margin: 0; padding: 0; box-sizing: border-box; }
        .pw-root ::selection { background: rgba(125,211,252,0.3); color: #fff; }

        /* Noise overlay */
        .pw-noise {
          position: fixed;
          inset: 0;
          z-index: 9999;
          pointer-events: none;
          opacity: 0.025;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
        }

        /* Floating background orbs */
        .pw-orb { position: fixed; border-radius: 50%; filter: blur(120px); pointer-events: none; z-index: 0; }
        .pw-orb--1 { width: min(600px, 80vw); height: min(600px, 80vw); background: rgba(0,212,255,0.05); top: -200px; left: -100px; animation: pw-drift 20s ease-in-out infinite; }
        .pw-orb--2 { width: min(500px, 70vw); height: min(500px, 70vw); background: rgba(168,85,247,0.05); top: -100px; right: -50px; animation: pw-drift 25s ease-in-out infinite reverse; }
        .pw-orb--3 { width: min(400px, 60vw); height: min(400px, 60vw); background: rgba(255,105,180,0.04); bottom: -200px; left: 30%; animation: pw-drift 22s ease-in-out infinite 5s; }
        @keyframes pw-drift {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -20px) scale(1.05); }
          66% { transform: translate(-20px, 15px) scale(0.95); }
        }

        .pw-wrap { max-width: 820px; margin: 0 auto; padding: 0 24px; position: relative; z-index: 1; }

        /* NAV */
        .pw-nav {
          position: sticky;
          top: 0;
          z-index: 100;
          padding: 14px 0;
          background: rgba(8,8,16,0.9);
          backdrop-filter: blur(24px) saturate(1.5);
          -webkit-backdrop-filter: blur(24px) saturate(1.5);
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }
        .pw-nav-inner {
          max-width: 1140px;
          margin: 0 auto;
          padding: 0 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .pw-logo { display: flex; align-items: center; text-decoration: none; }
        .pw-logo__img { height: 38px; width: auto; display: block; }
        .pw-nav-tag {
          font-size: 0.7rem;
          font-weight: 500;
          color: var(--pw-text-3);
          letter-spacing: 0.18em;
          text-transform: uppercase;
        }

        /* HERO */
        .pw-main { padding: 120px 0 80px; position: relative; z-index: 1; }
        .pw-hero { text-align: center; margin-bottom: 64px; }
        .pw-pill {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 16px;
          border: 1px solid rgba(125,211,252,0.15);
          border-radius: 100px;
          font-size: 0.72rem;
          font-weight: 600;
          color: var(--pw-accent);
          background: rgba(125,211,252,0.05);
          margin-bottom: 32px;
          backdrop-filter: blur(8px);
        }
        .pw-pill-dot {
          width: 6px; height: 6px;
          background: #22c55e;
          border-radius: 50%;
          box-shadow: 0 0 12px rgba(34,197,94,0.5);
          animation: pw-pulse 2s ease-in-out infinite;
        }
        @keyframes pw-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }

        .pw-h1 {
          font-size: clamp(2.6rem, 6vw, 4.4rem);
          font-weight: 900;
          letter-spacing: -0.045em;
          line-height: 1.08;
          margin-bottom: 28px;
        }
        .pw-h1__line1 { display: block; color: var(--pw-text); }
        .pw-h1__line2 {
          display: block;
          background: linear-gradient(135deg, #00D4FF 0%, #A855F7 40%, #FF69B4 70%, #FBBF24 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-top: 6px;
        }

        .pw-sub {
          font-size: 1.1rem;
          color: var(--pw-text-2);
          max-width: 540px;
          margin: 0 auto;
          line-height: 1.75;
          font-weight: 400;
        }
        .pw-sub strong { color: var(--pw-text); font-weight: 600; }

        /* Card */
        .pw-card {
          background: linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.01) 100%);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 20px;
          padding: 28px;
          backdrop-filter: blur(8px);
        }

        .pw-details-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 20px;
        }
        .pw-details-label {
          font-size: 0.7rem;
          font-weight: 600;
          color: var(--pw-text-3);
          letter-spacing: 0.16em;
          text-transform: uppercase;
        }
        .pw-details-meta { font-size: 0.75rem; color: var(--pw-text-3); }
        .pw-details-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
        .pw-details-grid .pw-k {
          font-size: 0.68rem;
          color: var(--pw-text-3);
          letter-spacing: 0.14em;
          text-transform: uppercase;
          margin-bottom: 6px;
        }
        .pw-details-grid .pw-v {
          font-size: 1.02rem;
          font-weight: 600;
          color: var(--pw-text);
        }

        .pw-section { margin-top: 64px; }
        .pw-section h2 {
          font-size: clamp(1.6rem, 3vw, 2.1rem);
          font-weight: 800;
          letter-spacing: -0.03em;
          text-align: center;
          margin-bottom: 40px;
          color: var(--pw-text);
        }

        /* Timeline */
        .pw-timeline { position: relative; }
        .pw-timeline::before {
          content: '';
          position: absolute;
          left: 23px;
          top: 24px;
          bottom: 24px;
          width: 1px;
          background: linear-gradient(180deg, rgba(125,211,252,0.4) 0%, rgba(125,211,252,0.05) 100%);
        }
        .pw-step { position: relative; display: flex; gap: 20px; padding-bottom: 32px; }
        .pw-step:last-child { padding-bottom: 0; }
        .pw-step__num {
          flex-shrink: 0;
          width: 48px;
          height: 48px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.95rem;
          background: rgba(8,8,16,0.9);
          border: 1px solid rgba(125,211,252,0.3);
          color: var(--pw-accent);
          position: relative;
          z-index: 1;
        }
        .pw-step--active .pw-step__num {
          background: rgba(125,211,252,0.12);
          border-color: var(--pw-accent);
          color: var(--pw-accent);
          box-shadow: 0 0 24px rgba(125,211,252,0.25);
        }
        .pw-step--future .pw-step__num {
          border-color: rgba(255,255,255,0.12);
          color: var(--pw-text-3);
        }
        .pw-step__body { flex: 1; padding-top: 4px; }
        .pw-step__title {
          font-size: 1.08rem;
          font-weight: 700;
          color: var(--pw-text);
          margin-bottom: 6px;
          letter-spacing: -0.01em;
        }
        .pw-step__body p {
          color: var(--pw-text-2);
          font-size: 0.95rem;
          line-height: 1.65;
        }
        .pw-step__body p strong { color: var(--pw-text); font-weight: 600; }
        .pw-step__meta {
          display: inline-block;
          margin-top: 10px;
          font-size: 0.72rem;
          font-weight: 600;
          color: var(--pw-accent);
          letter-spacing: 0.04em;
        }
        .pw-step--future .pw-step__meta { color: var(--pw-text-3); }

        /* Callout */
        .pw-callout {
          margin-top: 40px;
          position: relative;
          border-radius: 20px;
          padding: 1px;
          background: linear-gradient(135deg, rgba(0,212,255,0.25) 0%, rgba(168,85,247,0.15) 50%, rgba(255,105,180,0.15) 100%);
        }
        .pw-callout__inner {
          background: #0b0b15;
          border-radius: 19px;
          padding: 24px 28px;
          display: flex;
          gap: 16px;
          align-items: flex-start;
        }
        .pw-callout__icon {
          flex-shrink: 0;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: rgba(125,211,252,0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--pw-accent);
        }
        .pw-callout__title {
          font-weight: 700;
          color: var(--pw-text);
          margin-bottom: 6px;
          letter-spacing: -0.01em;
        }
        .pw-callout p {
          color: var(--pw-text-2);
          font-size: 0.92rem;
          line-height: 1.65;
        }

        /* CTA */
        .pw-contact {
          text-align: center;
          margin-top: 72px;
          padding-top: 48px;
          border-top: 1px solid rgba(255,255,255,0.06);
        }
        .pw-contact p { color: var(--pw-text-2); margin-bottom: 20px; font-size: 0.95rem; }
        .pw-btn-w {
          padding: 14px 32px;
          font-size: 0.88rem;
          font-weight: 600;
          color: var(--pw-bg);
          background: #fff;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.3s var(--pw-ease);
          font-family: var(--pw-font);
          box-shadow: 0 4px 24px rgba(255,255,255,0.08);
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 10px;
        }
        .pw-btn-w:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 40px rgba(125,211,252,0.18), 0 4px 24px rgba(255,255,255,0.12);
        }

        @media (max-width: 640px) {
          .pw-details-grid { grid-template-columns: 1fr 1fr; gap: 18px; }
          .pw-card { padding: 22px; }
          .pw-callout__inner { padding: 20px; }
        }
      `}</style>

      <div className="pw-root">
        <div className="pw-noise" />
        <div className="pw-orb pw-orb--1" />
        <div className="pw-orb pw-orb--2" />
        <div className="pw-orb pw-orb--3" />

        <nav className="pw-nav">
          <div className="pw-nav-inner">
            <a className="pw-logo" href="#" aria-label="Valutten">
              <img
                className="pw-logo__img"
                src="/valutten-logo.jpg"
                alt="VALUTTEN"
                style={{ mixBlendMode: 'lighten' }}
              />
            </a>
            <span className="pw-nav-tag">Pilot Confirmation</span>
          </div>
        </nav>

        <main className="pw-main">
          <div className="pw-wrap">
            <div className="pw-hero">
              <div className="pw-pill">
                <span className="pw-pill-dot" />
                Payment Received
              </div>
              <h1 className="pw-h1">
                <span className="pw-h1__line1">You're in.</span>
                <span className="pw-h1__line2">We'll take it from here.</span>
              </h1>
              <p className="pw-sub">
                Thanks for kicking off your <strong>2-month Valutten pilot</strong>.
                Our onboarding team handles everything — no setup required from you.
              </p>
            </div>

            <div className="pw-card">
              <div className="pw-details-head">
                <span className="pw-details-label">Pilot Details</span>
                <span className="pw-details-meta">Receipt sent via Stripe</span>
              </div>
              <div className="pw-details-grid">
                <div>
                  <div className="pw-k">Plan</div>
                  <div className="pw-v">Valutten Enterprise</div>
                </div>
                <div>
                  <div className="pw-k">Term</div>
                  <div className="pw-v">2-Month Pilot</div>
                </div>
                <div>
                  <div className="pw-k">Amount Paid</div>
                  <div className="pw-v">$1,594.00 AUD</div>
                </div>
              </div>
            </div>

            <section className="pw-section">
              <h2>What happens next</h2>

              <div className="pw-timeline">
                <div className="pw-step pw-step--active">
                  <div className="pw-step__num">1</div>
                  <div className="pw-step__body">
                    <div className="pw-step__title">Our team has been notified</div>
                    <p>Your payment triggered an alert to our onboarding team. We've already started preparing your environment.</p>
                    <span className="pw-step__meta">⏱ Automatic — just happened</span>
                  </div>
                </div>

                <div className="pw-step pw-step--future">
                  <div className="pw-step__num">2</div>
                  <div className="pw-step__body">
                    <div className="pw-step__title">You'll hear from us within 1 business day</div>
                    <p>
                      Expect an email from <strong>dieter@valutten.com</strong> to schedule
                      a 30-minute kickoff call if you would like.
                    </p>
                    <span className="pw-step__meta">📧 Check your inbox — add us to your safe senders list</span>
                  </div>
                </div>

                <div className="pw-step pw-step--future">
                  <div className="pw-step__num">3</div>
                  <div className="pw-step__body">
                    <div className="pw-step__title">We build your dashboard</div>
                    <p>Within 24 hours of kickoff, we load your historical commission data, configure your offices and brokers, and hand you a working dashboard — not a setup wizard.</p>
                    <span className="pw-step__meta">🛠 White-glove — you don't lift a finger</span>
                  </div>
                </div>

                <div className="pw-step pw-step--future">
                  <div className="pw-step__num">4</div>
                  <div className="pw-step__body">
                    <div className="pw-step__title">You get your login + live data</div>
                    <p>We send you your login, schedule a product walkthrough, and your 2-month pilot begins. We check in weekly.</p>
                  </div>
                </div>
              </div>
            </section>

            <div className="pw-callout">
              <div className="pw-callout__inner">
                <div className="pw-callout__icon">
                  <ShieldCheck size={20} />
                </div>
                <div>
                  <div className="pw-callout__title">You don't need an account yet</div>
                  <p>
                    Your pilot doesn't start until we've loaded your data and briefed you personally.
                    There's nothing to set up right now — if you try to log in you won't find an account.
                    That's by design. We'll provision it and send credentials after kickoff.
                  </p>
                </div>
              </div>
            </div>

            <div className="pw-contact">
              <p>Questions in the meantime?</p>
              <a className="pw-btn-w" href="mailto:dieter@valutten.com">
                <Mail size={18} />
                Email dieter@valutten.com
              </a>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default PilotWelcomePage;
